import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import moment from 'moment';
import {
  numberToCurrency,
  isNullOrUndefined,
  isNotEmptyArray,
} from 'utils/misc';
import { convertToFloat } from 'utils/numbers';
import cancellationTypes from 'constants/cancellationTypes';
import permissionTypes from 'constants/permissionTypes';

import commonPropTypes from '../common/commonPropTypes';
import FormLayout from '../common/FormLayout';
import ConfirmationModal from '../common/ConfirmationModal';
import { getCancellationFeeValidators } from '../common/validators';

import * as Styled from '../CancellationForm.styled';
import NoticePeriod from '../common/NoticePeriod';
import CancelFeeDeclaration from '../common/CancelFeeDeclaration';
import NoPermissionError from '../common/NoPermissionError';

const OverdueForm = (props) => {
  const {
    cancellationReasons,
    onFormSubmit,
    isUpdatingAccount,
    adfitNo,
    estimatedFinalBillDate,
    localisation,
    outstandingDsFee,
    account,
    facility,
    balanceToSettleWithoutCancelFee,
    cancelDaysNotice,
    permissions,
  } = props;

  const {
    outsideTerm,
    cancelFee,
    country,
    overdueAmount,
    isFixedTerm,
    isTermIdentified,
    hasPy,
    isBiPartite,
  } = account;

  const { industryType } = facility;

  const { dateFormat } = localisation.locales;

  const cancellationOptions = useMemo(() => {
    const endOfTermOption =
      // should be inside term and term should be identified
      // and only if has no PY and is outside term and TermType is Payment i.e. term not identified
      !outsideTerm || (!hasPy && outsideTerm && !isTermIdentified)
        ? [
            {
              label: 'Cancel at the end of the term',
              subLabels: [
                ...(estimatedFinalBillDate && hasPy
                  ? [
                      `Estimated date of final payment is ${moment(
                        estimatedFinalBillDate
                      ).format(dateFormat)}`,
                    ]
                  : []),
                'Any overdues might defer the cancellation',
              ],
              value: cancellationTypes.SET_TO_TERM,
            },
          ]
        : [];

    const noticerPeriodOption =
      outsideTerm && !isFixedTerm && isTermIdentified
        ? [
            {
              label: `Cancel after ${cancelDaysNotice} days notice`,
              subLabels: [
                ...(estimatedFinalBillDate && hasPy
                  ? [
                      `Estimated date of final payment is ${moment(
                        estimatedFinalBillDate
                      ).format(dateFormat)}`,
                    ]
                  : []),
                'Any overdues might defer the cancellation',
              ],
              value: cancellationTypes.WITH_NOTICE_PERIOD,
            },
          ]
        : [];

    //If the user has only one permission then show these options.
    if (
      permissions.includes(permissionTypes.canSetToTerm) &&
      !permissions.includes(permissionTypes.canCancelAllAccounts)
    ) {
      return [...endOfTermOption, ...noticerPeriodOption];
    }
    return [
      {
        label: 'Waive overdue amount and cancel',
        subLabels: [
          `Debitsuccess fee of ${numberToCurrency(
            isNullOrUndefined(outstandingDsFee) ? 0 : outstandingDsFee,
            localisation
          )} will be charged to the business`,
        ],
        value: cancellationTypes.CHARGE_BACK_DS_FEE,
      },
      //Do not delete this commented code. This is tested code but this option must be hidden temporarily(courtesy of genius product planning).
      // ...(outstandingDsFee
      //   ? [
      //       {
      //         label: 'Collect Debitsuccess fee and cancel',
      //         subLabels: [
      //           `Debitsuccess fee of ${numberToCurrency(
      //             outstandingDsFee,
      //             localisation
      //           )} will be charged to the customer`,
      //         ],
      //         value: cancellationTypes.COLLECT_DS_FEE,
      //       },
      //     ]
      //   : []),
      {
        label: 'Collect overdue amount and cancel',
        subLabels: [
          `Overdue amount of ${numberToCurrency(
            overdueAmount,
            localisation
          )} will be charged to the customer`,
        ],
        value: cancellationTypes.COLLECT_OVERDUE,
      },
      ...endOfTermOption,
      ...noticerPeriodOption,
    ];
  }, [
    localisation,
    estimatedFinalBillDate,
    outsideTerm,
    outstandingDsFee,
    overdueAmount,
    dateFormat,
    cancelDaysNotice,
    isFixedTerm,
    permissions,
  ]);

  //State for the confirmation Modal.
  const [confirmationModalState, setConfirmationModalState] = useState({
    isOpen: false,
    displayText: [],
  });

  const { handleSubmit, control, watch, errors, getValues, setValue } = useForm(
    {
      defaultValues: {
        cancellationOption: isNotEmptyArray(cancellationOptions)
          ? cancellationOptions[0].value
          : undefined,
        cancelReason: null,
        cancellationFee: null,
        noticeStartDate: moment(),
        cancellationFeeDeclaration: false,
      },
      mode: 'onBlur',
      reValidateMode: 'onBlur',
    }
  );

  const watchCancellationOption = watch('cancellationOption');

  const watchCancellationFee = watch('cancellationFee');

  const onValidSubmit = useCallback(
    (data) => {
      // Open the confirmation modal if required.
      if (
        data.cancellationOption === cancellationTypes.COLLECT_DS_FEE ||
        data.cancellationOption === cancellationTypes.CHARGE_BACK_DS_FEE
      ) {
        setConfirmationModalState({
          isOpen: true,
          displayText: [
            'By selecting this option you agree to waive fees owed to your business.',
          ],
        });
        return;
      }
      onFormSubmit(cancellationOptions, data);
    },
    [cancellationOptions, onFormSubmit]
  );

  const onConfirmModal = useCallback(() => {
    const data = getValues();
    setConfirmationModalState({
      displayText: [],
      isOpen: false,
    });
    onFormSubmit(cancellationOptions, data);
  }, [getValues, cancellationOptions, onFormSubmit]);

  const onCancelConfirmModal = useCallback(() => {
    setConfirmationModalState({
      isOpen: false,
      displayText: [],
    });
  }, [setConfirmationModalState]);

  const isEstimatedFinalBillDateToday = useMemo(
    () => moment(estimatedFinalBillDate).isSame(moment(), 'days'),
    [estimatedFinalBillDate]
  );

  if (!isNotEmptyArray(cancellationOptions)) {
    return <NoPermissionError />;
  }

  return (
    <>
      <ConfirmationModal
        isOpen={confirmationModalState.isOpen}
        onCancelAccountClick={onConfirmModal}
        onCloseClick={onCancelConfirmModal}
        displayText={confirmationModalState.displayText}
      />

      <FormLayout
        onSubmit={handleSubmit(onValidSubmit)}
        isSubmittingForm={isUpdatingAccount}
        formControl={control}
        adfitNo={adfitNo}
        dataTestId={'overdue-form'}
        cancellationOptions={cancellationOptions}
        cancellationReasons={cancellationReasons}>
        <div />
        {!outsideTerm &&
          hasPy &&
          !isEstimatedFinalBillDateToday &&
          watchCancellationOption === cancellationTypes.COLLECT_OVERDUE && (
            <FormLayout.InputWrapper>
              <Controller
                name={'cancellationFee'}
                control={control}
                rules={{
                  required: false,
                  min: {
                    value: 1.0,
                    message: 'Cancellation fee must be $1.00 or greater',
                  },
                  validate: {
                    ...getCancellationFeeValidators(getValues, {
                      cancelFee,
                      balanceToSettleWithoutCancelFee,
                      industryType,
                      country,
                      isBiPartite,
                    }),
                  },
                }}
                render={({ onChange, name, value, onBlur }) => (
                  <>
                    <Styled.LabelContainer>
                      <Styled.CancellationFeeLabel id={'feeLabel'}>
                        {'Cancellation fee '}
                        <Styled.CancellationFeeSideText>
                          {'(Optional)'}
                        </Styled.CancellationFeeSideText>
                      </Styled.CancellationFeeLabel>
                      <Styled.CancellationFeeSubLabel id={'feeDescription'}>
                        {
                          'Cancellation fee is in addition to the overdue amount'
                        }
                      </Styled.CancellationFeeSubLabel>
                    </Styled.LabelContainer>

                    <Styled.FormInput
                      name={name}
                      labelId={'feeLabel feeDescription'}
                      type={'number'}
                      step={0.01}
                      min={1.0}
                      onBlur={(event) => {
                        onBlur(event);
                        //Reset the declaration checkbox if on blur no cancellation fee is entered.
                        // Done because the field is optional.
                        if (!value)
                          setValue('cancellationFeeDeclaration', false);
                      }}
                      disabled={isUpdatingAccount}
                      value={isNullOrUndefined(value) ? '' : value}
                      onChange={(event) => {
                        if (event.target.validity.valid) {
                          onChange(convertToFloat(event.target.value));
                        }
                      }}
                      startAdornment={'$'}
                      showStartAdornmentDivider={true}
                      showInlineIcons={false}
                      error={
                        errors.cancellationFee && errors.cancellationFee.message
                      }
                    />
                  </>
                )}
              />
              <CancelFeeDeclaration
                cancelFeeLimit={cancelFee}
                formControl={control}
                cancellationFeeValue={watchCancellationFee}
                cancellationFeeError={errors.cancellationFee}
                declarationError={errors.cancellationFeeDeclaration}
              />
            </FormLayout.InputWrapper>
          )}

        {watchCancellationOption === cancellationTypes.WITH_NOTICE_PERIOD && (
          <FormLayout.InputWrapper>
            <NoticePeriod
              formControl={control}
              cancelDaysNotice={cancelDaysNotice}
              isSubmittingForm={isUpdatingAccount}
              dateDisplayFormat={dateFormat}
              noticeErrors={errors.noticeStartDate}
            />
          </FormLayout.InputWrapper>
        )}
      </FormLayout>
    </>
  );
};

OverdueForm.propTypes = {
  ...commonPropTypes,
  outstandingDsFee: PropTypes.number,
};

OverdueForm.defaultProps = {
  estimatedFinalBillDate: null,
  outstandingDsFee: null,
};

export default OverdueForm;
